import { codeForGroup, groupForCode } from '@/app/common/filters/session/filterCodes.ts';
import type {
  FilterSessionEntry,
  FilterSessionState,
  FilterSessionValue,
} from '@/app/common/filters/session/sessionState.ts';
import {
  SESSION_FORMAT_VERSION,
  SESSION_ENTRY_LIMIT,
  EMPTY_FILTER_SESSION,
  RECENTLY_USED_LIMIT,
  SEPARATOR,
  SESSION_LEADER_MAX_LENGTH,
} from '@/app/common/filters/session/sessionState.ts';

const SEPARATORS = Object.values(SEPARATOR);

const isClean = (value: string): boolean =>
  value.length > 0 &&
  value.length <= SESSION_LEADER_MAX_LENGTH &&
  encodeURIComponent(value) === value &&
  SEPARATORS.every((separator) => !value.includes(separator));

const serialisePair = (groupId: string, value?: string): string | undefined => {
  const code = codeForGroup(groupId);
  if (!code) return undefined;
  if (value === undefined || !isClean(value)) return code;
  return `${code}${SEPARATOR.VALUE}${value}`;
};

const parsePair = (raw: string): FilterSessionValue | undefined => {
  const [code, ...rest] = raw.split(SEPARATOR.VALUE);
  const groupId = groupForCode(code);
  if (!groupId || !rest.length) return undefined;

  const value = rest.join(SEPARATOR.VALUE);
  return isClean(value) ? { groupId, value } : undefined;
};

const serialiseEntry = (entry: FilterSessionEntry): string | undefined => serialisePair(entry.groupId, entry.leader);

const parseEntry = (raw: string): FilterSessionEntry | undefined => {
  const groupId = groupForCode(raw.split(SEPARATOR.VALUE)[0]);
  if (!groupId) return undefined;

  const pair = parsePair(raw);
  return pair ? { groupId, leader: pair.value } : { groupId };
};

const firstEntryPerGroup = (entries: FilterSessionEntry[]): FilterSessionEntry[] => {
  const seen = new Set<string>();
  return entries.filter((entry) => {
    if (seen.has(entry.groupId)) return false;
    seen.add(entry.groupId);
    return true;
  });
};

export const serialiseFilterSession = (state: FilterSessionState): string => {
  const entries = state.entries
    .slice(-SESSION_ENTRY_LIMIT)
    .map(serialiseEntry)
    .filter((entry) => !!entry);
  const chips = state.recentlyUsed
    .map((chip) => serialisePair(chip.groupId, chip.value))
    .filter((chip) => !!chip && chip.includes(SEPARATOR.VALUE))
    .slice(0, RECENTLY_USED_LIMIT);

  return [
    `${SESSION_FORMAT_VERSION}${entries.map((entry) => `${SEPARATOR.ENTRY}${entry}`).join('')}`,
    chips.join(SEPARATOR.CHIP),
  ].join(SEPARATOR.SECTION);
};

export const parseFilterSession = (raw?: string): FilterSessionState => {
  if (!raw) return EMPTY_FILTER_SESSION;

  const [filterSession = '', chips = ''] = raw.split(SEPARATOR.SECTION);
  const [version, ...entryParts] = filterSession.split(SEPARATOR.ENTRY);
  if (version !== SESSION_FORMAT_VERSION) return EMPTY_FILTER_SESSION;

  return {
    entries: firstEntryPerGroup(entryParts.map(parseEntry).filter((entry) => !!entry)).slice(-SESSION_ENTRY_LIMIT),
    recentlyUsed: (chips
      ? chips
          .split(SEPARATOR.CHIP)
          .map(parsePair)
          .filter((chip) => !!chip)
      : []
    ).slice(0, RECENTLY_USED_LIMIT),
  };
};
